import { BALLOON, CASH, FINANCE, LEASE } from '@makemydeal/dr-platform-types';

import type { ScenarioGridRow, ScenarioGridRowKey, ScenarioRowContext } from '../types/scenarioGridTypes';

const always = () => true;
const isLease = ({ offerType }: ScenarioRowContext) => offerType === LEASE;
const isFinanceOrBalloon = ({ offerType }: ScenarioRowContext) => offerType === FINANCE || offerType === BALLOON;
const isFlexBuy = ({ offerType, isFlexBuy }: ScenarioRowContext) => offerType === FINANCE && isFlexBuy;
const isDealerBalloon = ({ offerType, isBalloonEnabledForDealer }: ScenarioRowContext) =>
    offerType === BALLOON && isBalloonEnabledForDealer;

export const SCENARIO_GRID_ROWS: readonly ScenarioGridRow[] = [
    { key: 'retailPrice', label: 'MSRP', appliesTo: always },
    { key: 'sellingPrice', label: 'Selling Price', appliesTo: always },
    { key: 'paymentGrid', label: 'Payment Grid', appliesTo: ({ isPaymentGridOn }) => isPaymentGridOn },
    { key: 'cashDown', label: 'Cash Down', appliesTo: always },
    {
        key: 'cashDueToday',
        label: 'Cash Due Today',
        appliesTo: (context) => isLease(context) && context.isEquityAllocationOrderEnabled
    },
    { key: 'term', label: 'Term', appliesTo: ({ offerType }) => offerType !== CASH },
    { key: 'annualMiles', label: 'Annual Miles', appliesTo: isLease },
    { key: 'flexBuyPercent', label: 'Flex Buy %', appliesTo: isFlexBuy },
    { key: 'incentives', label: 'Incentives', appliesTo: always },
    { key: 'rate', label: 'Rate', appliesTo: ({ offerType }) => offerType !== CASH },
    { key: 'residual', label: 'Residual', appliesTo: isLease },
    { key: 'adjResidual', label: 'Adj Residual', appliesTo: isLease },
    { key: 'mrm', label: 'MRM', appliesTo: isLease },
    { key: 'acqFee', label: 'Acq Fee', appliesTo: isLease },
    { key: 'securityDeposit', label: 'Security Deposit', appliesTo: isLease },
    {
        key: 'lenderProgram',
        label: 'Lender Program',
        appliesTo: (context) => context.offerType !== CASH && !isFlexBuy(context)
    },
    { key: 'daysToFirstPayment', label: 'Days to 1st Payment', appliesTo: isFinanceOrBalloon },
    { key: 'firstPaymentDate', label: 'First Payment Date', appliesTo: isFinanceOrBalloon },
    {
        key: 'secondPaymentDate',
        label: '2nd Payment Date',
        appliesTo: (context) => isLease(context) && context.isLeaseContractFieldsEnabled
    },
    {
        key: 'paymentFrequency',
        label: 'Payment Frequency',
        appliesTo: (context) => isLease(context) && context.isOnePayLeaseEnabled
    },
    { key: 'flexBuyFirstPayments', label: 'First Payments', appliesTo: isFlexBuy },
    { key: 'flexBuyNextPayments', label: 'Next Payments', appliesTo: isFlexBuy },
    { key: 'flexBuyAvgPayment', label: 'Avg. Monthly Payment', appliesTo: isFlexBuy },
    { key: 'flexBuyComparable', label: 'Comparable Month', appliesTo: isFlexBuy },
    { key: 'balloonPayment', label: 'Balloon Payment', appliesTo: isDealerBalloon },
    { key: 'balloonTermType', label: 'Balloon Term Type', appliesTo: isDealerBalloon },
    { key: 'tradeAllowance', label: 'Trade Allowance', appliesTo: always },
    { key: 'tradePayoff', label: 'Trade Payoff', appliesTo: always },
    {
        key: 'netCashPayout',
        label: 'Net Cash Payout',
        appliesTo: ({ offerType, hasNetCashPayout }) => offerType === CASH && hasNetCashPayout
    },
    { key: 'accessories', label: 'Accessories', appliesTo: always },
    { key: 'protectionProducts', label: 'Protection Products', appliesTo: always },
    { key: 'fees', label: 'Fees', appliesTo: always },
    { key: 'taxes', label: 'Taxes', appliesTo: always },
    {
        key: 'dueAtSigning',
        label: 'Due at Signing',
        appliesTo: (context) => isLease(context) && context.isDueAtSigningEnhancementsEnabled
    },
    { key: 'paidByCustomer', label: 'Paid by Customer', appliesTo: isLease },
    { key: 'outsideLienholder', label: 'Outside Lienholder', appliesTo: ({ offerType }) => offerType === CASH }
];

export const SCENARIO_GRID_ROWS_BY_KEY = Object.fromEntries(SCENARIO_GRID_ROWS.map((row) => [row.key, row])) as Record<
    ScenarioGridRowKey,
    ScenarioGridRow
>;
